import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  useGetChatConversationsQuery,
  useGetOrCreateDmConversationMutation,
  useCreateGroupConversationMutation,
  useGetConversationMessagesQuery,
  useMarkConversationReadMutation,
  useSendConversationMessageMutation,
  useLazyGetConversationByIdQuery,
} from "../../redux/services/chat/chatApi";
import { useChatSocket } from "../../contexts/ChatContext";
import { useDispatch } from "react-redux";
import { notificationApi } from "../../redux/services/notifications/notificationApi";
import { useAuth } from "../../hooks/useAuth";
import {
  XMarkIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  PlusIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import classNames from "classnames";
import CommunicationsTimeline from "../CommunicationsTimeline";
import Button from "../../shared/Button";
import Input from "../../shared/Input";
import ChatComposer from "./ChatComposer";
import Avatar from "../../shared/Avatar";
import { useGetUsersQuery } from "../../redux/services/users/usersApi";
import { useLazySearchChatReferencesQuery } from "../../redux/services/chat/chatReferencesApi";
import { useConfig } from "../../hooks";
import {
  getLatestOutboundReceiptState,
  getUnreadCountForUser,
} from "../../redux/services/chat/readReceipts";

/**
 * Resolve the conversation title and the "other user" for DMs.
 * @param {Object} conversation
 * @param {string} currentUserId
 * @returns {{ title: string, otherUser: any | null }}
 */
function getConversationTitleAndOther(conversation, currentUserId) {
  const participants = conversation?.participants || [];
  const otherUser =
    conversation?.type === "dm"
      ? participants.find((p) => String(p?._id) !== String(currentUserId)) ||
        null
      : null;

  const title =
    conversation?.type === "group"
      ? conversation.title || "Group"
      : otherUser?.fullName || "Direct message";

  return { title, otherUser };
}

function ConversationAvatar({ conversation, currentUserId }) {
  const participants = conversation?.participants || [];
  const isGroup = conversation?.type === "group";

  if (!isGroup) {
    const { otherUser } = getConversationTitleAndOther(
      conversation,
      currentUserId
    );
    return (
      <Avatar
        size="xs"
        src={otherUser?.avatar || null}
        name={otherUser?.fullName || "User"}
        className="shrink-0"
      />
    );
  }

  // Simple stacked avatar group (first 3 participants excluding self)
  const others = participants
    .filter((p) => String(p?._id) !== String(currentUserId))
    .slice(0, 3);

  return (
    <div className="flex -space-x-2">
      {others.map((p) => (
        <Avatar
          key={p?._id || p?.email || p?.fullName}
          size="xs"
          src={p?.avatar || null}
          name={p?.fullName || "User"}
          className="border-2 border-white"
        />
      ))}
    </div>
  );
}

function ConversationList({ conversations, onOpen, currentUserId }) {
  return (
    <div className="p-2 space-y-2 overflow-y-auto max-h-[60vh]">
      {conversations.map((c) => {
        const { title } = getConversationTitleAndOther(c, currentUserId);
        const unreadCount = getUnreadCountForUser(c, currentUserId);
        // Build context label using humanId if available, fallback to last 6 chars of entityId
        const contextLabel = (() => {
          if (!c.context?.entityType) return null;
          const entityType = c.context.entityType;
          const humanId = c.context.humanId;
          const entityId = c.context.entityId;

          // Map entity types to display labels
          const typeLabels = {
            job: "Job",
            leg: "Leg",
            drop: "Drop",
            manifest: "Manifest",
            payout: "Payout",
            invoice: "Invoice",
          };

          const displayType = typeLabels[entityType] || entityType;
          const displayId =
            humanId || (entityId ? String(entityId).slice(-6) : null);

          return displayId ? `${displayType}: #${displayId}` : null;
        })();
        return (
          <button
            key={c._id}
            type="button"
            onClick={() => onOpen(c)}
            className="w-full text-left p-2 rounded hover:bg-gray-50 border border-gray-200"
          >
            <div className="flex items-center gap-2">
              <div className="relative">
                <ConversationAvatar
                  conversation={c}
                  currentUserId={currentUserId}
                />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 inline-flex w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium text-gray-900 truncate">
                    {title}
                  </div>
                  {contextLabel && (
                    <div className="text-[10px] text-gray-500 shrink-0">
                      {contextLabel}
                    </div>
                  )}
                </div>
                <div className="text-xs text-gray-500 truncate">
                  {c.lastMessagePreview || "No messages yet"}
                </div>
              </div>
            </div>
          </button>
        );
      })}
      {!conversations.length && (
        <div className="text-sm text-gray-500 p-2">No conversations yet.</div>
      )}
    </div>
  );
}

function ChatBox({ conversation, onClose }) {
  const { joinConversation, leaveConversation, isConnected } = useChatSocket();
  const { user } = useAuth();
  const { appType } = useConfig();
  const [draft, setDraft] = useState("");
  const scrollRef = useRef(null);
  const textareaRef = useRef(null);

  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStart, setMentionStart] = useState(null);
  const [mentionType, setMentionType] = useState(null);
  const [triggerSearch, mentionSearchState] =
    useLazySearchChatReferencesQuery();
  const mentionResults = mentionSearchState?.data?.results || [];
  const mentionDropdownRef = useRef(null);

  const {
    data: messagesResp,
    isLoading: isMessagesLoading,
    isFetching: isMessagesFetching,
    isUninitialized: isMessagesUninitialized,
  } = useGetConversationMessagesQuery(
    {
      conversationId: conversation._id,
      limit: 50,
    },
    {
      // Fallback polling to keep chat fresh even if socket misses events.
      pollingInterval: isConnected ? 0 : 5000,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );

  const messages = useMemo(
    () => messagesResp?.data || messagesResp || [],
    [messagesResp]
  );
  const [sendMessage, sendState] = useSendConversationMessageMutation();

  // Avoid flashing the empty state on first open while messages are still loading.
  const isInitialMessagesLoad =
    !isMessagesUninitialized &&
    !messagesResp &&
    (isMessagesLoading || isMessagesFetching);

  React.useEffect(() => {
    if (!conversation?._id) return;
    if (isConnected) {
      joinConversation?.(conversation._id);
    }
    return () => leaveConversation?.(conversation._id);
  }, [conversation?._id, isConnected, joinConversation, leaveConversation]);

  const { title } = getConversationTitleAndOther(conversation, user?._id);
  const { latestOutboundMessageId, readReceiptLabel } = useMemo(
    () =>
      getLatestOutboundReceiptState({
        conversation,
        messages,
        currentUserId: user?._id,
      }),
    [conversation, messages, user?._id]
  );

  const timelineItems = useMemo(() => {
    return (messages || []).map((m) => ({
      description: m.content,
      timestamp: new Date(
        m.dateCreated || m.createdAt || Date.now()
      ).toISOString(),
      type: "message",
      sender: {
        name: m.author?.fullName || "User",
        avatar: m.author?.avatar || null,
      },
      isOutbound: String(m.author?._id) === String(user?._id),
      isFullWidth: false,
      readReceiptLabel:
        readReceiptLabel && String(m._id) === String(latestOutboundMessageId)
          ? readReceiptLabel
          : undefined,
    }));
  }, [messages, user?._id, latestOutboundMessageId, readReceiptLabel]);

  // Always keep the viewport anchored to the newest messages (bottom).
  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    // Wait for paint so heights are correct
    requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
  }, [conversation?._id, messages?.length]);

  const mentionMode =
    mentionOpen && mentionType && !String(mentionQuery || "").trim()
      ? "recent"
      : "search";

  // Mention search debounce
  React.useEffect(() => {
    if (!mentionOpen) return;
    const q = String(mentionQuery || "").trim();

    // If user typed only a type (e.g. @job), show recents for that type
    if (mentionType && mentionMode === "recent") {
      triggerSearch({
        q: "",
        appType,
        limitPerType: 5,
        type: mentionType,
        mode: "recent",
      });
      return;
    }

    // Otherwise, only search after 2+ characters of query
    if (q.length < 2) return;

    const t = setTimeout(() => {
      triggerSearch({
        q,
        appType,
        limitPerType: 5,
        type: mentionType || undefined,
        mode: "search",
      });
    }, 200);
    return () => clearTimeout(t);
  }, [
    mentionOpen,
    mentionQuery,
    mentionType,
    mentionMode,
    appType,
    triggerSearch,
  ]);

  /**
   * Close the mention dropdown without selecting, and return focus to textarea.
   */
  function closeMention() {
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStart(null);
    setMentionType(null);
    textareaRef.current?.focus();
  }

  // Close mention dropdown on Escape key or click outside
  React.useEffect(() => {
    if (!mentionOpen) return;

    function handleKeyDown(e) {
      if (e.key === "Escape") {
        e.preventDefault();
        closeMention();
      }
    }

    function handleClickOutside(e) {
      // If click is outside the mention dropdown, close it
      if (
        mentionDropdownRef.current &&
        !mentionDropdownRef.current.contains(e.target) &&
        textareaRef.current &&
        !textareaRef.current.contains(e.target)
      ) {
        closeMention();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mentionOpen]);

  function parseMentionToken(tokenRaw) {
    const raw = String(tokenRaw || "");
    // Allow: job, job123, job 123
    const m = raw.match(/^([a-zA-Z]+)\s*([^\s]*)$/);
    if (!m) return { type: null, query: "" };
    const t = (m[1] || "").toLowerCase();
    const q = (m[2] || "").trim();

    // Recognized types (singular + plural)
    const aliases = {
      job: "job",
      jobs: "job",
      manifest: "manifest",
      manifests: "manifest",
      invoice: "invoice",
      invoices: "invoice",
      payout: "payout",
      payouts: "payout",
      equipment: "equipment",
      client: "client",
      clients: "client",
      user: "user",
      users: "user",
      leg: "leg",
      legs: "leg",
    };

    return { type: aliases[t] || null, query: q };
  }

  function detectMention(nextValue, cursorPos) {
    const pos = typeof cursorPos === "number" ? cursorPos : nextValue.length;
    const before = nextValue.slice(0, pos);
    const at = before.lastIndexOf("@");
    if (at === -1) return { open: false };
    // Require mention to start at beginning or after whitespace
    if (at > 0 && /\S/.test(before[at - 1])) return { open: false };

    const tokenRaw = before.slice(at + 1);

    // If text after @ starts with '[', it's an existing reference token - don't open mention
    if (tokenRaw.startsWith("[")) return { open: false };

    // If the @ is followed by a completed reference token, don't open mention
    // Check if we're inside or right after a complete token @[type:id|label]
    const afterAt = nextValue.slice(at);
    if (/^@\[[a-z]+:[^\]|]+\|[^\]]+\]/.test(afterAt)) return { open: false };

    const parsed = parseMentionToken(tokenRaw);
    return {
      open: true,
      query: parsed.query || "",
      start: at,
      type: parsed.type,
    };
  }

  /**
   * Handle draft text changes and detect @mentions.
   */
  function handleDraftChange(nextValue) {
    setDraft(nextValue);
    // Detect mention at the end of the text (for contenteditable)
    const meta = detectMention(nextValue, nextValue.length);
    if (meta.open) {
      setMentionOpen(true);
      setMentionQuery(meta.query || "");
      setMentionStart(meta.start);
      setMentionType(meta.type || null);
    } else {
      setMentionOpen(false);
      setMentionQuery("");
      setMentionStart(null);
      setMentionType(null);
    }
  }

  /**
   * Handle @ button click - insert @ at cursor position and trigger mention mode.
   */
  function handleAtClick() {
    const el = textareaRef.current;
    const pos = el?.selectionStart ?? draft.length;
    const newDraft = `${draft.slice(0, pos)}@${draft.slice(pos)}`;
    setDraft(newDraft);
    setMentionOpen(true);
    setMentionQuery("");
    setMentionStart(pos);
    setMentionType(null);
    // Focus the input and position cursor after the @
    requestAnimationFrame(() => {
      el?.focus?.();
      const newPos = pos + 1;
      el?.setSelectionRange?.(newPos, newPos);
    });
  }

  function applyReference(refItem) {
    const el = textareaRef.current;
    const cursor = el?.selectionStart ?? draft.length;
    const start =
      typeof mentionStart === "number" ? mentionStart : draft.lastIndexOf("@");
    const token = `@[${refItem.type}:${refItem.id}|${refItem.label}] `;
    const next = `${draft.slice(0, start)}${token}${draft.slice(cursor)}`;
    setDraft(next);
    setMentionOpen(false);
    setMentionQuery("");
    setMentionStart(null);
    setMentionType(null);
    requestAnimationFrame(() => {
      try {
        el?.focus?.();
        const newPos = start + token.length;
        el?.setSelectionRange?.(newPos, newPos);
      } catch {
        // ignore
      }
    });
  }

  async function handleSend() {
    const text = draft.trim();
    if (!text) return;
    setDraft("");
    await sendMessage({ conversationId: conversation._id, content: text });
    // Ensure we stay at bottom after sending
    const el = scrollRef.current;
    if (el) {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    }
  }

  return (
    <div className="w-[360px] h-[420px] bg-white border border-gray-200 rounded-2xl shadow-lg flex flex-col overflow-hidden">
      <div className="px-3 py-2 border-b flex items-center justify-between bg-gray-50">
        <div className="font-semibold text-sm text-gray-800 truncate">
          {title}
        </div>
        <button type="button" onClick={onClose}>
          <XMarkIcon className="h-5 w-5 text-gray-500 hover:text-gray-800" />
        </button>
      </div>
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-1"
        style={{ minHeight: 0 }}
      >
        <CommunicationsTimeline
          items={timelineItems}
          loading={
            isInitialMessagesLoad || (sendState.isLoading && !messages?.length)
          }
        />
      </div>
      {/* Chat Input Area */}
      <div className="border-t p-3 bg-white relative">
        {/* Mention dropdown - positioned above the input area */}
        {mentionOpen && (
          <div
            ref={mentionDropdownRef}
            className="absolute bottom-full left-3 right-3 mb-1 z-10"
          >
            <div className="bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
              <div className="px-3 py-2 text-xs text-gray-500 border-b flex items-center justify-between">
                <span>
                  {mentionType
                    ? `Reference ${mentionType}s`
                    : "Type to search references (e.g. @job, @manifest)"}{" "}
                  — select to insert a link.
                </span>
                <button
                  type="button"
                  onClick={closeMention}
                  className="ml-2 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600"
                  title="Close"
                  aria-label="Close reference search"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
              {mentionMode === "search" &&
              String(mentionQuery || "").trim().length < 2 ? (
                <div className="p-3 text-sm text-gray-500">Keep typing…</div>
              ) : mentionSearchState.isFetching ? (
                <div className="p-3 text-sm text-gray-500">Searching…</div>
              ) : mentionResults.length ? (
                <div className="divide-y">
                  {mentionResults.slice(0, 20).map((r) => (
                    <button
                      key={`${r.type}:${r.id}`}
                      type="button"
                      onClick={() => applyReference(r)}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50 transition-colors"
                    >
                      <div className="text-sm font-medium text-gray-900 truncate">
                        {r.label}
                      </div>
                      <div className="text-[11px] text-gray-500 uppercase">
                        {r.type}
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="p-3 text-sm text-gray-500">No matches.</div>
              )}
            </div>
          </div>
        )}

        {/* Rich text composer with integrated @ button */}
        <ChatComposer
          value={draft}
          onChange={handleDraftChange}
          onAtClick={handleAtClick}
          onSend={handleSend}
          sendLoading={sendState.isLoading}
          sendDisabled={!draft.trim()}
          placeholder="Type a message..."
          inputRef={textareaRef}
        />
      </div>
    </div>
  );
}

export default function ChatWidget({
  open,
  onClose,
  onUnreadCountChange,
  onOpen,
}) {
  const { user } = useAuth();
  const dispatch = useDispatch();
  const { pendingConversationId, clearPendingConversation } = useChatSocket();
  const { data, isLoading } = useGetChatConversationsQuery(
    {},
    {
      // Keep unread counts + notifications fresh even when widget is closed.
      pollingInterval: 10000,
      refetchOnReconnect: true,
      refetchOnFocus: true,
    }
  );
  const conversations = data?.data || [];
  const [getOrCreateDm] = useGetOrCreateDmConversationMutation();
  const [createGroup, createGroupState] = useCreateGroupConversationMutation();
  const [markRead] = useMarkConversationReadMutation();
  const [fetchConversationById] = useLazyGetConversationByIdQuery();
  const [openBoxes, setOpenBoxes] = useState([]);
  const [isMinimized, setIsMinimized] = useState(false);
  const [activeView, setActiveView] = useState("list"); // "list" | "newChat" | "newGroup"
  const [userSearch, setUserSearch] = useState("");

  // Group chat creation state
  const [groupName, setGroupName] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]); // Array of user objects

  const shouldSearchUsers = userSearch.trim().length > 1;
  const needsUserList = activeView === "newChat" || activeView === "newGroup";
  const { data: usersData, isLoading: usersLoading } = useGetUsersQuery(
    {
      page: 1,
      limit: 20,
      search: true,
      model: "User",
      searchTerm: shouldSearchUsers ? userSearch.trim() : undefined,
    },
    { skip: !needsUserList || !shouldSearchUsers }
  );

  const candidateUsers = useMemo(() => {
    const list = usersData?.users || [];
    const selectedIds = new Set(
      selectedUsers.map((u) => String(u?._id || u?.id))
    );
    return (list || [])
      .filter((u) => {
        const uid = String(u?._id || u?.id);
        // Exclude self
        if (uid === String(user?._id)) return false;
        // In newGroup view, also exclude already selected users
        if (activeView === "newGroup" && selectedIds.has(uid)) return false;
        return true;
      })
      .slice(0, 20);
  }, [usersData, user?._id, activeView, selectedUsers]);

  const totalUnread = useMemo(() => {
    return (conversations || []).reduce(
      (sum, c) => sum + getUnreadCountForUser(c, user?._id),
      0
    );
  }, [conversations, user?._id]);

  const markNotificationsReadForConversation = React.useCallback(
    (conversationId) => {
      if (!conversationId) return;
      let markedCount = 0;
      dispatch(
        notificationApi.util.updateQueryData(
          "getNotifications",
          { page: 1, limit: 50 },
          (draft) => {
            const items = draft?.data?.notifications || [];
            items.forEach((notification) => {
              const meta = notification?.metadata || {};
              const convoId = meta.conversationId || meta.conversation_id;
              if (
                !notification.read &&
                notification.type === "CHAT" &&
                String(convoId) === String(conversationId)
              ) {
                notification.read = true;
                markedCount += 1;
              }
            });
          }
        )
      );

      if (markedCount > 0) {
        dispatch(
          notificationApi.util.updateQueryData(
            "getNotificationStats",
            undefined,
            (draft) => {
              if (!draft?.data) return;
              const currentUnread = Number(draft.data.unread || 0);
              draft.data.unread = Math.max(0, currentUnread - markedCount);
            }
          )
        );
      }
    },
    [dispatch]
  );

  React.useEffect(() => {
    onUnreadCountChange?.(totalUnread);
  }, [totalUnread, onUnreadCountChange]);

  /**
   * Handle opening a specific conversation from external triggers (e.g., notifications)
   * When pendingConversationId is set in ChatContext, find or fetch the conversation and open it
   */
  useEffect(() => {
    if (!pendingConversationId) return;

    const handlePendingConversation = async () => {
      // First, try to find the conversation in the already-loaded list
      let convo = (conversations || []).find(
        (c) => String(c._id) === String(pendingConversationId)
      );

      // If not found in the list, fetch it from the API
      if (!convo) {
        try {
          const result = await fetchConversationById(
            pendingConversationId
          ).unwrap();
          convo = result?.data || result;
        } catch (err) {
          console.error("Failed to fetch conversation:", err);
          clearPendingConversation();
          return;
        }
      }

      if (convo?._id) {
        // Trigger onOpen to ensure the ChatWidget is open
        onOpen?.();
        // Open the conversation box
        setOpenBoxes((prev) => {
          if (prev.some((c) => c._id === convo._id)) return prev;
          return [...prev, convo].slice(-3);
        });
        setIsMinimized(true);
        setActiveView("list");
        // Mark as read
        markRead({ conversationId: convo._id });
        markNotificationsReadForConversation(convo._id);
      }

      // Clear the pending state
      clearPendingConversation();
    };

    handlePendingConversation();
  }, [
    pendingConversationId,
    conversations,
    clearPendingConversation,
    fetchConversationById,
    markRead,
    onOpen,
  ]);

  function openConversation(convo) {
    setOpenBoxes((prev) => {
      if (prev.some((c) => c._id === convo._id)) return prev;
      return [...prev, convo].slice(-3); // keep up to 3 open like Messenger
    });
    // Default: collapse menu after open
    setIsMinimized(true);
    setActiveView("list");

    // Clear unread for this conversation when opened
    if (convo?._id) {
      markRead({ conversationId: convo._id });
      markNotificationsReadForConversation(convo._id);
    }
  }

  async function handleStartDm(otherUserId) {
    try {
      const res = await getOrCreateDm({ otherUserId }).unwrap();
      const convo = res?.data || res;
      if (convo?._id) openConversation(convo);
    } catch (e) {
      // fail silently here; full toast behavior can be added later if desired
      // eslint-disable-next-line no-console
      console.error("Failed to start DM", e);
    }
  }

  /**
   * Create a new group conversation with the selected users.
   * Requires at least 2 selected users (3 total including current user).
   */
  async function handleCreateGroup() {
    if (selectedUsers.length < 2) return;
    try {
      const res = await createGroup({
        participants: selectedUsers.map((u) => String(u?._id || u?.id)),
        title: groupName.trim() || "Group chat",
      }).unwrap();
      const convo = res?.data || res;
      // Reset group creation state
      setGroupName("");
      setSelectedUsers([]);
      setUserSearch("");
      // Open the new group conversation
      if (convo?._id) openConversation(convo);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Failed to create group", e);
    }
  }

  function handleCloseMenu() {
    setIsMinimized(false);
    setActiveView("list");
    onClose?.();
  }

  // When the full menu panel is visible, shift chat boxes left so they don't overlap.
  const isMenuExpanded = Boolean(open && !isMinimized);
  // Menu sits at `right-5` (1.25rem) with `w-80` (20rem). Add ~1rem gap.
  const chatBoxesRightOffset = isMenuExpanded ? "22.5rem" : "5rem";

  return (
    <>
      {open && (
        <div
          className={classNames(
            "fixed bottom-20 right-5 z-[60] bg-white border border-gray-200 shadow-lg overflow-hidden",
            {
              "w-80 rounded-lg": !isMinimized,
              "w-14 rounded-full": isMinimized,
            }
          )}
          style={{
            right: "calc(1.25rem + var(--right-sidebar-offset, 0px))",
          }}
        >
          {isMinimized ? (
            <div className="bg-primary-600 text-white">
              <div className="flex flex-col items-center gap-2 py-2">
                <button
                  type="button"
                  onClick={() => setIsMinimized(false)}
                  className="h-9 w-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                  title="Expand messages"
                >
                  <ChevronUpIcon className="h-4 w-4" />
                </button>

                <div className="flex flex-col gap-2 items-center pb-2">
                  {(conversations || []).slice(0, 3).map((c) => {
                    const unreadCount = getUnreadCountForUser(c, user?._id);
                    return (
                      <button
                        key={c._id}
                        type="button"
                        onClick={() => openConversation(c)}
                        className="relative"
                        title="Open conversation"
                      >
                        <ConversationAvatar
                          conversation={c}
                          currentUserId={user?._id}
                        />
                        {unreadCount > 0 && (
                          <span className="absolute -top-1 -right-1 inline-flex w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-primary-600" />
                        )}
                      </button>
                    );
                  })}
                </div>

                <button
                  type="button"
                  onClick={handleCloseMenu}
                  className="h-8 w-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center mb-2"
                  title="Close messages"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Header: keep minimal; controls live in footer */}
              <div className="px-3 py-2 border-b bg-white">
                <div className="font-semibold text-sm text-gray-800">
                  Messages
                </div>
              </div>

              <>
                {activeView === "newChat" ? (
                  <div className="p-2">
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveView("list");
                          setUserSearch("");
                        }}
                        className="h-8 w-8 shrink-0 rounded-md hover:bg-gray-100 inline-flex items-center justify-center text-gray-600"
                        title="Back"
                        aria-label="Back to conversations"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <Input
                        size="sm"
                        clearable
                        placeholder="Search users…"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e?.value ?? e ?? "")}
                        wrapperClasses="flex-1"
                      />
                    </div>

                    {/* New Group button */}
                    <button
                      type="button"
                      onClick={() => setActiveView("newGroup")}
                      className="w-full text-left p-2 mb-2 rounded hover:bg-gray-50 border border-dashed border-gray-300 text-primary-600 font-medium text-sm"
                    >
                      + New Group
                    </button>

                    <div className="space-y-2 max-h-[50vh] overflow-y-auto">
                      {usersLoading ? (
                        <div className="p-2 text-sm text-gray-500">
                          Loading…
                        </div>
                      ) : (
                        candidateUsers.map((u) => (
                          <button
                            key={u?._id || u?.id}
                            type="button"
                            onClick={() =>
                              handleStartDm(String(u?._id || u?.id))
                            }
                            className="w-full text-left p-2 rounded hover:bg-gray-50 border border-gray-200 flex items-center gap-2"
                          >
                            <Avatar
                              size="xs"
                              src={u?.avatar || null}
                              name={u?.fullName || u?.email || "User"}
                            />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {u?.fullName || u?.email || "User"}
                              </div>
                              {u?.email && (
                                <div className="text-xs text-gray-500 truncate">
                                  {u.email}
                                </div>
                              )}
                            </div>
                          </button>
                        ))
                      )}

                      {!usersLoading && !candidateUsers.length && (
                        <div className="text-sm text-gray-500 p-2">
                          {shouldSearchUsers
                            ? "No users found."
                            : "Type at least 2 characters to search users."}
                        </div>
                      )}
                    </div>
                  </div>
                ) : activeView === "newGroup" ? (
                  <div className="p-2">
                    {/* Header with back button */}
                    <div className="flex items-center gap-2 mb-2">
                      <button
                        type="button"
                        onClick={() => {
                          setActiveView("newChat");
                          setUserSearch("");
                          setGroupName("");
                          setSelectedUsers([]);
                        }}
                        className="h-8 w-8 shrink-0 rounded-md hover:bg-gray-100 inline-flex items-center justify-center text-gray-600"
                        title="Back"
                        aria-label="Back to new chat"
                      >
                        <ChevronLeftIcon className="h-4 w-4" />
                      </button>
                      <span className="text-sm font-semibold text-gray-800">
                        New Group
                      </span>
                    </div>

                    {/* Group name input */}
                    <div className="mb-2">
                      <Input
                        size="sm"
                        placeholder="Group name (optional)"
                        value={groupName}
                        onChange={(e) => setGroupName(e?.value ?? e ?? "")}
                      />
                    </div>

                    {/* Selected users chips */}
                    {selectedUsers.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2 p-2 bg-gray-50 rounded-md">
                        {selectedUsers.map((u) => (
                          <span
                            key={u?._id || u?.id}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-primary-100 text-primary-800 rounded-full text-xs"
                          >
                            <Avatar
                              size="xs"
                              src={u?.avatar || null}
                              name={u?.fullName || u?.email || "User"}
                              className="!w-4 !h-4"
                            />
                            <span className="truncate max-w-[80px]">
                              {u?.fullName || u?.email || "User"}
                            </span>
                            <button
                              type="button"
                              onClick={() =>
                                setSelectedUsers((prev) =>
                                  prev.filter(
                                    (x) =>
                                      String(x?._id || x?.id) !==
                                      String(u?._id || u?.id)
                                  )
                                )
                              }
                              className="hover:text-primary-900"
                              title="Remove"
                            >
                              <XMarkIcon className="h-3 w-3" />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    {/* User search */}
                    <div className="mb-2">
                      <Input
                        size="sm"
                        clearable
                        placeholder="Search users to add…"
                        value={userSearch}
                        onChange={(e) => setUserSearch(e?.value ?? e ?? "")}
                      />
                    </div>

                    {/* User list with checkboxes */}
                    <div className="space-y-1 max-h-[35vh] overflow-y-auto mb-2">
                      {usersLoading ? (
                        <div className="p-2 text-sm text-gray-500">
                          Loading…
                        </div>
                      ) : (
                        candidateUsers.map((u) => (
                          <button
                            key={u?._id || u?.id}
                            type="button"
                            onClick={() =>
                              setSelectedUsers((prev) => [...prev, u])
                            }
                            className="w-full text-left p-2 rounded hover:bg-gray-50 border border-gray-200 flex items-center gap-2"
                          >
                            <Avatar
                              size="xs"
                              src={u?.avatar || null}
                              name={u?.fullName || u?.email || "User"}
                            />
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {u?.fullName || u?.email || "User"}
                              </div>
                              {u?.email && (
                                <div className="text-xs text-gray-500 truncate">
                                  {u.email}
                                </div>
                              )}
                            </div>
                            <PlusIcon className="h-4 w-4 text-gray-400" />
                          </button>
                        ))
                      )}

                      {!usersLoading &&
                        !candidateUsers.length &&
                        shouldSearchUsers && (
                          <div className="text-sm text-gray-500 p-2">
                            No users found.
                          </div>
                        )}
                      {!usersLoading &&
                        !shouldSearchUsers &&
                        !candidateUsers.length && (
                          <div className="text-sm text-gray-500 p-2">
                            Type at least 2 characters to search users.
                          </div>
                        )}
                    </div>

                    {/* Create group button */}
                    <Button
                      type="primary"
                      size="sm"
                      className="w-full"
                      disabled={selectedUsers.length < 2}
                      loading={createGroupState.isLoading}
                      onClick={handleCreateGroup}
                    >
                      Create Group ({selectedUsers.length} selected)
                    </Button>
                    {selectedUsers.length < 2 && (
                      <p className="text-xs text-gray-500 mt-1 text-center">
                        Select at least 2 users
                      </p>
                    )}
                  </div>
                ) : isLoading ? (
                  <div className="p-3 text-sm text-gray-500">Loading…</div>
                ) : (
                  <ConversationList
                    conversations={conversations}
                    onOpen={openConversation}
                    currentUserId={user?._id}
                  />
                )}
              </>

              {/* Footer: controls + unread */}
              <div className="px-2 py-1.5 border-t bg-primary-600 text-white">
                <div className="flex items-center justify-between gap-2">
                  {/* Left: New */}
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-1.5 rounded-md bg-white/10 hover:bg-white/20"
                    onClick={() => {
                      if (activeView === "list") {
                        setActiveView("newChat");
                      } else {
                        // From newChat or newGroup, go back to list
                        setActiveView("list");
                        setUserSearch("");
                        setGroupName("");
                        setSelectedUsers([]);
                      }
                    }}
                    title="New chat"
                    aria-label="New chat"
                  >
                    <PlusIcon className="h-4 w-4" />
                    New
                  </button>

                  {/* Right: unread + collapse + close */}
                  <div className="flex items-center gap-1.5">
                    {totalUnread > 0 && (
                      <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-red-500 rounded-full">
                        {totalUnread > 99 ? "99+" : totalUnread}
                      </span>
                    )}
                    <button
                      type="button"
                      onClick={() => setIsMinimized(true)}
                      title="Collapse to side"
                      aria-label="Collapse to side"
                      className="h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 inline-flex items-center justify-center"
                    >
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={handleCloseMenu}
                      title="Close messages"
                      aria-label="Close messages"
                      className="h-8 w-8 rounded-md bg-white/10 hover:bg-white/20 inline-flex items-center justify-center"
                    >
                      <XMarkIcon className="h-5 w-5 text-white/90 hover:text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {openBoxes.length > 0 && (
        <div
          className="fixed bottom-5 z-[60] flex gap-3 items-end"
          style={{
            right: `calc(${chatBoxesRightOffset} + var(--right-sidebar-offset, 0px))`,
          }}
        >
          {openBoxes.map((c) => (
            <ChatBox
              key={c._id}
              conversation={
                conversations.find((conversation) => String(conversation._id) === String(c._id)) ||
                c
              }
              onClose={() =>
                setOpenBoxes((prev) => prev.filter((x) => x._id !== c._id))
              }
            />
          ))}
        </div>
      )}
    </>
  );
}
